import { PerspectiveCamera, Box3, Object3D, Vector3 } from 'three'

export interface CameraSettings {
  FOV?: number
  NEAR?: number
  FAR?: number
  // ZOOM: number
}

const CAMERA_DEFAULT_SETTINGS: Required<CameraSettings> = {
  FOV: 75,
  NEAR: 0.1,
  FAR: 1000,
}

export const initializeCamera = (
  aspectRatio: number,
  settings?: CameraSettings
) => {
  const finalSettings = Object.assign(
    {},
    CAMERA_DEFAULT_SETTINGS,
    settings ?? {}
  )
  const camera = new PerspectiveCamera(
    finalSettings.FOV,
    aspectRatio,
    finalSettings.NEAR,
    finalSettings.FAR
  )
  camera.name = 'MainCamera'
  // camera.position.set(0, 0, CAMERA_SETTINGS.ZOOM)
  return camera
}

/**
 * Fits camera to the bounding box of the object based on a ratio and optionally rotates around the object
 * @param camera Camera to be fitted against the object
 * @param object The object to fit the camera against
 * @param fitRatio How close the fit should be, larger value is further away from object
 * @param rotation The rotation (in radians) to apply to the camera [will orbit around object]
 * @param fitPerRad How much the rotation should add to the fit ratio per radian
 */
export const fitCameraToObject = (
  camera: PerspectiveCamera,
  object: Object3D,
  fitRatio = 1.2,
  rotation = 0,
  fitPerRad = 1 / 4
) => {
  const box = new Box3()

  box.expandByObject(object)

  const size = box.getSize(new Vector3())
  const center = box.getCenter(new Vector3())

  const maxSize = Math.max(size.x, size.y, size.z)
  const fitHeightDistance =
    maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360))
  const fitWidthDistance = fitHeightDistance / camera.aspect
  const distance =
    (fitRatio + rotation * fitPerRad) *
    Math.max(fitHeightDistance, fitWidthDistance)

  const direction = new Vector3(0, 0, -1)
    .applyAxisAngle(object.up, -rotation)
    .multiplyScalar(distance)

  camera.near = distance / 100
  camera.far = distance * 100
  camera.updateProjectionMatrix()

  camera.position.copy(center).sub(direction)
  camera.lookAt(object.position)
}
